import { convertToRedirectUrl } from '~/src/lib/helpers'
// `dupe.link` is left out on purpose: its shortlinks are not `/redirect`
// paths, so the path check below would skip them anyway.
// `link_clicks.metadata.src` for report buy links. Lives here, not in the
// retailer-links hook, so components can use it without pulling in the fetcher.
export const SEO_REPORT_CLICK_SOURCE = 'seo-report'
// Same, for shop and brand pages cited in a report's sources list.
export const SEO_REPORT_SOURCE_CLICK_SOURCE = 'seo-report-source'

const DUPE_HOSTS = new Set(['dupe.com', 'www.dupe.com'])
const REDIRECT_PATH = /^\/redirect(\/|$)/

/**
 * Adds a `src` label to a dupe.com `/redirect` link, so the click lands in
 * `link_clicks.metadata.src`. The `/redirect` page forwards `src` from its own
 * query to the API (see `buildRedirectLink`).
 *
 * Unlike `convertToRedirectUrl`, this never reads `window`, so the href is the
 * same in server and client markup. Retailer URLs, and links that already
 * carry a `src`, come back unchanged.
 */
export function withRedirectClickSource(url: string, src: string): string {
  if (!url) return url

  let parsed: URL
  try {
    parsed = new URL(url, 'https://dupe.com')
  } catch {
    return url
  }

  // Relative paths resolve onto the dupe.com base, so one hostname check
  // covers both. A protocol-relative `//host/...` resolves to `host` and is
  // rejected here, rather than being mistaken for a local path.
  if (!DUPE_HOSTS.has(parsed.hostname)) return url
  if (!REDIRECT_PATH.test(parsed.pathname)) return url
  if (parsed.searchParams.has('src')) return url

  const hashIndex = url.indexOf('#')
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const fragment = hashIndex === -1 ? '' : url.slice(hashIndex)
  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}src=${encodeURIComponent(src)}${fragment}`
}

/**
 * A buy-link href that always reaches `/redirect`: dupe.com redirect links get
 * their `src` label as above, and a raw retailer URL — which would otherwise go
 * out unmonetized and unlogged — becomes a relative `/redirect?link=…&src=…`.
 * Still window-free (that branch of `convertToRedirectUrl` reads no `window`),
 * so server and client markup match.
 */
export function toBuyLinkHref(url: string, src: string): string {
  if (!/^https?:\/\//i.test(url)) return withRedirectClickSource(url, src)

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }
  // Any of our own hosts (api., staging., dupe.link) is already a dupe link:
  // label it if it can be labelled, never wrap it a second time.
  const host = parsed.hostname
  if (
    host === 'dupe.com' ||
    host.endsWith('.dupe.com') ||
    host === 'dupe.link'
  ) {
    return withRedirectClickSource(url, src)
  }
  // convertToRedirectUrl takes any `/redirect` path for one of ours without
  // checking the host, so a shop's own `/redirect/…` page would go out unwrapped.
  if (REDIRECT_PATH.test(parsed.pathname)) {
    return `/redirect?${new URLSearchParams({ link: url, src })}`
  }
  return convertToRedirectUrl(url, { source: src })
}
